import type { Proposta } from '@/types'
import type { LayoutConfig, PlanData } from '@/utils/dealCalculations'
import { defaultLayoutConfig, mergePlans } from '@/utils/dealCalculations'
import { buildExportPlanData } from '@/utils/proposalExport'
import { mergeCategoryCoordMaps, type CatalogPlan } from '@/utils/planCatalog'
import { detectTemplateType } from './proposalTabCatalog'

function mergePlanLayoutConfigs(plans: PlanData[]): LayoutConfig | undefined {
  if (plans.length === 0) return undefined
  let merged: LayoutConfig = { ...defaultLayoutConfig, ...(plans[0].layoutConfig || {}) }
  for (let i = 1; i < plans.length; i += 1) {
    const cfg = plans[i].layoutConfig
    if (!cfg) continue
    merged = {
      ...merged,
      ...cfg,
      categoryCoords: mergeCategoryCoordMaps(merged.categoryCoords, cfg.categoryCoords),
    }
  }
  return merged
}

export function convertPropostaToPlanData(
  proposal: Proposta,
  dealServico: string | undefined,
  catalogPlans: CatalogPlan[],
): PlanData {
  const plan = buildExportPlanData(proposal, { dealServico, catalogPlans })
  return { ...plan, templateType: detectTemplateType(plan.nome) }
}

export function mergedPropostaToPlanData(
  proposal: Proposta,
  dealServico: string | undefined,
  catalogPlans: CatalogPlan[],
): PlanData {
  const proposalItens = proposal.itens || proposal.items || []
  const proposalCombos = proposal.combos || []

  if (proposalCombos.length > 0 && proposalItens.length > 0) {
    const comboPlans: PlanData[] = []
    const usedIds = new Set<string>()

    for (const combo of proposalCombos) {
      const comboItens = proposalItens.filter((i) => combo.itemIds.includes(i.id))
      if (comboItens.length === 0) continue
      comboItens.forEach((i) => usedIds.add(i.id))
      const comboProposal = {
        ...proposal,
        itens: comboItens,
        totalFinal: comboItens.reduce((a, i) => a + i.total, 0),
      }
      comboPlans.push(convertPropostaToPlanData(comboProposal, dealServico, catalogPlans))
    }

    const orphanItens = proposalItens.filter((i) => !usedIds.has(i.id))
    if (orphanItens.length > 0) {
      const orphanProposal = {
        ...proposal,
        itens: orphanItens,
        totalFinal: orphanItens.reduce((a, i) => a + i.total, 0),
      }
      comboPlans.push(convertPropostaToPlanData(orphanProposal, dealServico, catalogPlans))
    }

    if (comboPlans.length > 1) {
      const merged = mergePlans(comboPlans)
      const totalFinal = Number(proposal.totalFinal ?? merged.pricing.valor)
      return {
        id: merged.id,
        nome: merged.nome,
        descricao: merged.descricao,
        tipo: merged.tipo,
        servicos: merged.servicos,
        pricing: {
          valor: totalFinal,
          valorMensal: merged.pricing.valorMensal,
          recorrente: merged.pricing.recorrente,
          duracao: merged.pricing.duracao,
          total: totalFinal,
        },
        validade: proposal.validade,
        templateType: detectTemplateType(merged.nome),
        showBonus: true,
        bonusText: 'BÔNUS: Pacote Combo com condições especiais',
        paymentDetails:
          comboPlans
            .map((p) => `${p.nome}: ${p.paymentDetails || ''}`)
            .filter(Boolean)
            .join(' · ') || 'Pagamento mensal via boleto ou PIX com vencimento a combinar.',
        loyaltyDuration: merged.pricing.recorrente
          ? 'Período mínimo sugerido de 12 meses.'
          : undefined,
        layoutConfig: mergePlanLayoutConfigs(comboPlans),
      }
    }

    if (comboPlans.length === 1) return comboPlans[0]
  }

  return convertPropostaToPlanData(proposal, dealServico, catalogPlans)
}
